import React, { useEffect, useRef } from "react";
import { useLanguage } from "@/App";
import { ArrowRight, Play, Award, Users, Briefcase } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";

const Hero3D = () => {
  const { t, language } = useLanguage();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#030303]"
    >
      {/* Premium Background */}
      <div className="absolute inset-0">
        {/* Gradient mesh */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,107,0,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,69,0,0.1),transparent_50%)]" />
        
        {/* Animated grid */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,107,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,107,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px'
          }}
        />
        
        {/* Large glowing orb */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,107,0,0.3) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
      </div>

      {/* Floating geometric elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large rotating ring */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/3 right-[10%] w-64 h-64 border border-[#FF6B00]/20 rounded-full"
        />
        
        {/* Second ring */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/3 right-[10%] w-48 h-48 border border-[#FF6B00]/10 rounded-full"
          style={{ marginTop: '32px', marginRight: '-32px' }}
        />
        
        {/* Floating squares */}
        <motion.div
          animate={{ 
            y: [-20, 20, -20],
            rotate: [0, 90, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[20%] left-[8%] w-16 h-16 border border-[#FF6B00]/30"
          style={{ transform: 'rotate(45deg)' }}
        />
        
        <motion.div
          animate={{ 
            y: [20, -20, 20],
            rotate: [45, 135, 45]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[30%] right-[15%] w-12 h-12 bg-[#FF6B00]/10 border border-[#FF6B00]/20"
        />

        {/* Glowing particles */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -40, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
            className="absolute w-1 h-1 bg-[#FF6B00] rounded-full"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${20 + Math.random() * 60}%`,
              boxShadow: '0 0 6px #FF6B00'
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <motion.div 
        style={{ y, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20"
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text content */}
          <div>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-8"
            >
              <span className="flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/30 px-4 py-2 text-[#FF6B00] text-xs font-bold uppercase tracking-[0.2em]">
                <Award size={14} />
                {language === "nl" ? "Premium Digital Agency" : "Premium Digital Agency"}
              </span>
            </motion.div>

            {/* Main headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] mb-8"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              <span className="text-white">{language === "nl" ? "Wij Bouwen" : "We Build"}</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9500] via-[#FF6B00] to-[#FF4500]">
                {language === "nl" ? "Digitale" : "Digital"}
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
                {language === "nl" ? "Meesterwerken" : "Masterpieces"}
              </span>
            </motion.h1>

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-zinc-400 leading-relaxed max-w-lg mb-10"
            >
              {language === "nl" 
                ? "Van geavanceerde AI-oplossingen tot adembenemende 3D web ervaringen. Wij transformeren jouw visie in digitale realiteit."
                : "From advanced AI solutions to breathtaking 3D web experiences. We transform your vision into digital reality."}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4 mb-12"
            >
              <a
                href="#contact"
                className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white px-8 py-4 text-sm font-bold tracking-wide overflow-hidden transition-all hover:shadow-[0_0_40px_rgba(255,107,0,0.4)]"
              >
                <span className="relative z-10">{language === "nl" ? "Start Je Project" : "Start Your Project"}</span>
                <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#showcase"
                className="group inline-flex items-center gap-3 bg-white/5 border border-[#FF6B00]/30 text-white px-8 py-4 text-sm font-bold tracking-wide hover:bg-[#FF6B00]/10 hover:border-[#FF6B00]/50 transition-all"
              >
                <Play size={16} className="text-[#FF6B00]" />
                {language === "nl" ? "Bekijk Portfolio" : "View Portfolio"}
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex gap-12"
            >
              {[
                { icon: Briefcase, value: "150+", label: language === "nl" ? "Projecten" : "Projects" },
                { icon: Users, value: "15+", label: language === "nl" ? "Experts" : "Experts" },
                { icon: Award, value: "98%", label: language === "nl" ? "Tevreden" : "Satisfied" },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon size={20} className="text-[#FF6B00] mx-auto mb-2" />
                  <p className="text-2xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {stat.value}
                  </p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Visual showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            {/* Main showcase card */}
            <div className="relative">
              {/* Glow behind */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FF6B00]/20 to-[#FF4500]/20 blur-2xl" />
              
              {/* Card */}
              <div className="relative bg-[#0A0A0A] border border-[#FF6B00]/20 p-2 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                  alt="Premium web design showcase"
                  className="w-full aspect-[4/3] object-cover"
                />
                
                {/* Overlay content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse" />
                    <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-wider">Live Project</span>
                  </div>
                  <p className="text-white font-bold">E-Commerce Platform</p>
                  <p className="text-zinc-400 text-sm">3D Visualization • AI Integration</p>
                </div>
              </div>

              {/* Floating mini cards */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 bg-[#0A0A0A] border border-[#FF6B00]/30 p-3 shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#FF6B00] to-[#FF4500] rounded flex items-center justify-center text-white text-xs font-bold">
                    AI
                  </div>
                  <div>
                    <p className="text-white text-xs font-bold">AI Powered</p>
                    <p className="text-zinc-500 text-[10px]">Smart Solutions</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-4 -left-6 bg-[#0A0A0A] border border-[#FF6B00]/30 p-3 shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-700 rounded flex items-center justify-center text-white text-xs font-bold">
                    3D
                  </div>
                  <div>
                    <p className="text-white text-xs font-bold">3D WebGL</p>
                    <p className="text-zinc-500 text-[10px]">Immersive UX</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[#FF6B00] text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#FF6B00] to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero3D;
